"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Loader as Loader2, Send } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateRequestForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType, setProjectType] = useState("");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const validateZip = (value: string) => {
    return /^\d{5}$/.test(value.trim());
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    let hasError = false;
    if (!validateZip(zip)) {
      setZipError("Please enter a valid 5-digit US ZIP code.");
      hasError = true;
    } else {
      setZipError("");
    }
    if (!consent) {
      setConsentError("Consent is required to proceed.");
      hasError = true;
    } else {
      setConsentError("");
    }
    if (!projectType) {
      hasError = true;
    }
    if (hasError) return;
    setIsSubmitting(true);
    try {
      if (!LEAD_WEBHOOK_URL) {
        throw new Error("Webhook endpoint unconfigured");
      }
      const payload = new URLSearchParams({
        name,
        phone,
        email,
        project_type: projectType,
        address: zip,
        zip,
        details: message,
        timing,
        budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: typeof window !== "undefined" ? window.location.href : "",
        form_location: "about-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        throw new Error("Server responded with an error");
      }
    } catch {
      setSubmitError("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative rounded-2xl border border-border bg-card/85 backdrop-blur-md p-6 sm:p-10 lg:p-12 shadow-xl">
          <div className="mb-8 md:mb-10 text-center">
            <span className="text-xs uppercase font-mono tracking-wider text-primary font-semibold">
              Personalized Consultation
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-serif font-bold text-foreground">
              Request an Estimate
            </h2>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
              Share details about your upcoming Brookhaven renovation project and our design team will prepare a preliminary plan.
            </p>
          </div>

          <AnimatePresence mode="wait">
            {submitSuccess ? <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} className="py-12 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
                  <CheckCircle2 className="size-8" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-foreground">
                  Estimate Request Received
                </h3>
                <p className="mt-2 text-muted-foreground max-w-md mx-auto">
                  Thank you for contacting Brookhaven Elevation Renovations. A project manager will review your notes and reach out shortly.
                </p>
              </motion.div> : <motion.form key="form" onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="estimate-name">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-name" type="text" required placeholder="Jane Doe" value={name} onChange={e => setName(e.target.value)} className="bg-background/80" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-phone">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-phone" type="tel" required placeholder="(404) 555-0199" value={phone} onChange={e => setPhone(e.target.value)} className="bg-background/80" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="estimate-email">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-email" type="email" required placeholder="jane@example.com" value={email} onChange={e => setEmail(e.target.value)} className="bg-background/80" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-zip">
                      ZIP Code <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-zip" type="text" required maxLength={5} placeholder="30319" value={zip} onChange={e => {
                  setZip(e.target.value);
                  if (zipError) setZipError("");
                }} aria-invalid={!!zipError} className="bg-background/80" />
                    {zipError && <p className="text-xs text-destructive mt-1 font-medium flex items-center gap-1">
                        <AlertCircle className="size-3 shrink-0" />
                        {zipError}
                      </p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="estimate-project-type">
                      Project Type <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <select id="estimate-project-type" required value={projectType} onChange={e => setProjectType(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background/80 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                        <option value="" disabled>
                          Select project type
                        </option>
                        {PROJECT_OPTIONS.map(opt => <option key={opt} value={opt}>
                            {opt}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-timing">Preferred Timing</Label>
                    <select id="estimate-timing" value={timing} onChange={e => setTiming(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background/80 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:border-ring text-foreground">
                      <option value="">Select timeframe</option>
                      {TIMING_OPTIONS.map(opt => <option key={opt} value={opt}>
                          {opt}
                        </option>)}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-budget">Budget Range</Label>
                    <select id="estimate-budget" value={budget} onChange={e => setBudget(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background/80 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:border-ring text-foreground">
                      <option value="">Select budget</option>
                      {BUDGET_OPTIONS.map(opt => <option key={opt} value={opt}>
                          {opt}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimate-details">
                    Project Details / Message
                  </Label>
                  <Textarea id="estimate-details" rows={4} placeholder="Tell us about your home, layout goals, or specific materials you envision..." value={message} onChange={e => setMessage(e.target.value)} className="bg-background/80 resize-y" />
                </div>

                <div className="pt-2">
                  <div className="flex items-start space-x-3">
                    <Checkbox id="estimate-consent" checked={consent} onCheckedChange={checked => {
                  setConsent(checked === true);
                  if (consentError) setConsentError("");
                }} className="mt-0.5" />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="estimate-consent" className="text-xs sm:text-sm font-normal text-muted-foreground leading-snug cursor-pointer select-none">
                        I agree to be contacted about my project by phone, text, or email.{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      {consentError && <p className="text-xs text-destructive font-medium flex items-center gap-1">
                          <AlertCircle className="size-3 shrink-0" />
                          {consentError}
                        </p>}
                    </div>
                  </div>
                </div>

                {submitError && <Alert variant="destructive" className="mt-4">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Submission Notice</AlertTitle>
                    <AlertDescription className="text-xs sm:text-sm">
                      {submitError}
                    </AlertDescription>
                  </Alert>}

                <div className="pt-2 flex justify-end">
                  <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto font-medium">
                    {isSubmitting ? <>
                        <Loader2 className="size-4 animate-spin mr-2" />
                        Submitting...
                      </> : <>
                        Request Estimate
                        <Send className="size-4 ml-1" />
                      </>}
                  </Button>
                </div>
              </motion.form>}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>;
}